import { isValidElement, cloneElement, useCallback, memo } from 'react';
import {
  Toolbar,
  Typography,
  Tooltip,
  IconButton,
  Grow,
  Link,
  Box,
} from '@mui/material';
import { DeleteIcon } from '../../assets';
import { useStyles } from './toolbar.styles';
import { ObjectWithId } from '../../types';
import { useDispatch, selectAllItems, clearSelectedIds } from '../../state';

interface Props<TRecord extends ObjectWithId = ObjectWithId> {
  numSelected: number;
  ids: Array<number | string>;
  rows: Record<string, TRecord>;
  selectedIds: Array<number | string>;
  selectedRows: Record<string, TRecord>;
  bulkActionButtons?: React.ReactElement<any> | false;
  numberOfRows?: number;
  resource?: string;
  baseUrl?: string;
  filter?: object;
  order?: string | number;
  orderBy?: string;
  sortable?: boolean;
}

export const ListToolbar = memo(({ bulkActionButtons, ...props }: Props) => {
  const { classes, cx } = useStyles();
  const dispatch = useDispatch();

  const {
    numSelected,
    ids,
    numberOfRows,
    resource,
    baseUrl,
    filter,
    order,
    orderBy,
    sortable,
  } = props;

  const allOnPageSelected = numSelected > 0 && numSelected === ids.length;
  const hasMorePages =
    numberOfRows !== undefined && numberOfRows > ids.length;
  const allItemsSelected = numSelected === numberOfRows;

  const handleSelectAll = useCallback(() => {
    if (resource) {
      dispatch(
        selectAllItems({
          resource,
          baseUrl,
          filter,
          order,
          orderBy,
          sortable,
          numberOfRows,
        }),
      );
    }
  }, [resource, baseUrl, filter, order, orderBy, sortable, numberOfRows, dispatch]);

  const handleClearSelection = useCallback(() => {
    if (resource) {
      dispatch(clearSelectedIds({ resource }));
    }
  }, [resource, dispatch]);

  return (
    <Grow in={numSelected > 0} mountOnEnter unmountOnExit>
      <Toolbar className={cx(classes.root, classes.highlight)}>
        <Box className={classes.selectAllContainer}>
          <Typography
            className={classes.title}
            color='inherit'
            variant='h5'
            component='div'
          >
            {numSelected} selected
          </Typography>
          {allOnPageSelected && hasMorePages && !allItemsSelected && (
            <Typography
              variant='body2'
              component='span'
              className={classes.selectAllBanner}
            >
              All {ids.length} items on this page are selected.{' '}
              <Link
                component='button'
                variant='body2'
                onClick={handleSelectAll}
                className={classes.selectAllLink}
              >
                Select all {numberOfRows} items
              </Link>
            </Typography>
          )}
          {allItemsSelected && hasMorePages && (
            <Typography
              variant='body2'
              component='span'
              className={classes.selectAllBanner}
            >
              All {numberOfRows} items are selected.{' '}
              <Link
                component='button'
                variant='body2'
                onClick={handleClearSelection}
                className={classes.selectAllLink}
              >
                Clear selection
              </Link>
            </Typography>
          )}
        </Box>
        {isValidElement(bulkActionButtons) ? (
          cloneElement(bulkActionButtons, { ...props })
        ) : (
          <BulkDeleteButton selectedIds={props.selectedIds} />
        )}
      </Toolbar>
    </Grow>
  );
});

export const BulkDeleteButton = memo(
  ({ selectedIds }: { selectedIds: Array<number | string> }) => {
    const handleDelete = useCallback(() => {
      // eslint-disable-next-line no-console
      console.log(selectedIds);
      // Implement delete. Optimistic rendering?
    }, [selectedIds]);

    return (
      <Tooltip title='Delete'>
        <IconButton aria-label='delete' onClick={handleDelete} size='large'>
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    );
  },
);
